/**
 * EPC Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { InlineFilterBar, InlineSelect, InlineToggles } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { TipoDeRisco } from '../../../types/epc';

export interface EpcFiltersData {
  searchTerm: string;
  tipoDeRisco: string;
  expired: boolean;
  expiringSoon: boolean;
}

interface Props {
  onFilter: (filters: EpcFiltersData) => void;
  loading?: boolean;
  total?: number;
}

const RISK_LABELS: Record<string, string> = {
  [TipoDeRisco.FISICO]: 'Físico',
  [TipoDeRisco.QUIMICO]: 'Químico',
  [TipoDeRisco.BIOLOGICO]: 'Biológico',
  [TipoDeRisco.ERGONOMICO]: 'Ergonômico',
  [TipoDeRisco.ACIDENTE]: 'Acidente',
};

const RISK_OPTIONS = Object.values(TipoDeRisco).map((t) => ({
  value: t,
  label: RISK_LABELS[t],
}));

const defaultValues: EpcFiltersData = {
  searchTerm: '',
  tipoDeRisco: '',
  expired: false,
  expiringSoon: false,
};

export const EPCFiltersComponent: React.FC<Props> = ({
  onFilter,
  loading = false,
  total,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      tipoDeRisco: 'Risco',
    },
    formatValue: {
      tipoDeRisco: (v: string) => RISK_LABELS[v] ?? v,
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome, risco, método..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="EPCs"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineSelect
        label="Tipo de Risco"
        value={filters.tipoDeRisco}
        options={RISK_OPTIONS}
        onChange={(v) => setFilter('tipoDeRisco', v)}
        emptyLabel="Todos"
        minWidth={160}
      />
      <InlineToggles
        disabled={loading}
        items={[
          {
            label: 'Validade Vencida',
            checked: filters.expired,
            onChange: (v) => setFilter('expired', v),
          },
          {
            label: 'Vencendo (30 dias)',
            checked: filters.expiringSoon,
            onChange: (v) => setFilter('expiringSoon', v),
          },
        ]}
      />
    </InlineFilterBar>
  );
};

export default EPCFiltersComponent;
